import { useState, useEffect } from "react";
import { T } from "../../config/theme.js";
import { Card, SectionTitle } from "../ui/index.jsx";
import { api } from "../../hooks/useApi.js";
import { AlertTriangle, CheckCircle, ChevronRight } from "lucide-react";

export default function PrincipleDriftCard({ token, onGoDetail }) {
  const [drifts, setDrifts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    api("/principles/drifts/all", {}, token)
      .then(data => setDrifts(data?.drifts || []))
      .catch(() => setDrifts([]))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return null;

  // 최근 24시간 이탈만 필터
  const recent = drifts.filter(d => {
    const age = Date.now() - new Date(d.created_at).getTime();
    return age < 24 * 60 * 60 * 1000;
  });

  // 종목별 그룹화
  const byStock = {};
  recent.forEach(d => {
    if (!byStock[d.stock_code]) byStock[d.stock_code] = [];
    byStock[d.stock_code].push(d);
  });

  const stockCodes = Object.keys(byStock);

  // 이탈 없으면 카드 자체를 표시하지 않음
  if (stockCodes.length === 0) return null;

  // 이탈 있으면 경고 카드
  return (
    <Card style={{ marginBottom: 20, borderLeft: `3px solid ${T.yellow}` }}>
      <SectionTitle icon="AlertTriangle">원칙 이탈 현황</SectionTitle>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {stockCodes.map(code => {
          const items = byStock[code];
          const stockName = items[0]?.stock_name || code;
          return (
            <div
              key={code}
              onClick={() => onGoDetail?.(code)}
              style={{
                padding: "12px 14px",
                background: T.bg,
                borderRadius: 10,
                border: `1px solid ${T.yellow}20`,
                cursor: onGoDetail ? "pointer" : "default",
                transition: "border-color 0.2s",
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.yellow + "50"}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.yellow + "20"}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <AlertTriangle size={14} color={T.yellow} />
                  <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{stockName}</span>
                  <span style={{ fontSize: 11, color: T.textMuted }}>{code}</span>
                </div>
                {onGoDetail && <ChevronRight size={16} color={T.textMuted} />}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {items.slice(0, 3).map((d, i) => (
                  <div key={i} style={{ fontSize: 12, color: T.textDim, lineHeight: 1.5 }}>
                    {d.message || `${ruleLabel(d.rule)}: 기준 ${d.threshold} → 현재 ${d.current_value}`}
                  </div>
                ))}
                {items.length > 3 && (
                  <div style={{ fontSize: 11, color: T.textMuted }}>외 {items.length - 3}건</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function ruleLabel(rule) {
  const labels = {
    loss_limit: "손절선",
    target_profit: "목표 수익률",
    max_hold_days: "보유 기간",
    max_volatility: "변동성",
    disclosure: "공시",
    news: "뉴스",
  };
  return labels[rule] || rule;
}
